/**
 * useConversationTabs Hook
 * Manages tab state for individual conversations
 */

import React from 'react';
import { TabItem } from '@/constants/chatScreenData';
import { ChatMessage } from '@/types/chatTypes';

export interface ResponsePair {
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
}

export function useConversationTabs(
  tabs: TabItem[],
  activeTab: string | undefined,
  onTabChange: ((tabId: string, conversationId?: string) => void) | undefined,
  responsePairs: ResponsePair[]
) {
  // Track active tab per conversation (using assistant message ID as key)
  const [conversationTabs, setConversationTabs] = React.useState<
    Record<string, string>
  >({});

  // Get the last response pair ID
  const lastResponsePairId = React.useMemo(() => {
    if (responsePairs.length > 0) {
      return responsePairs[responsePairs.length - 1].assistantMessage.id;
    }
    return null;
  }, [responsePairs]);

  // Get active tab for a specific conversation (default to 'discover' so content is never blank)
  const getActiveTabForConversation = React.useCallback(
    (conversationId: string) => {
      const tab = conversationTabs[conversationId] || tabs[0]?.id || 'discover';
      return tab || 'discover';
    },
    [conversationTabs, tabs]
  );

  // Handle tab change for a specific conversation
  const handleConversationTabChange = React.useCallback(
    (tabId: string, conversationId: string, isLastPair: boolean) => {
      // Update the conversation's tab state
      setConversationTabs(prev => ({
        ...prev,
        [conversationId]: tabId,
      }));

      // Only call onTabChange for the current/last conversation
      if (
        isLastPair &&
        lastResponsePairId &&
        lastResponsePairId === conversationId
      ) {
        onTabChange?.(tabId);
      }
    },
    [onTabChange, lastResponsePairId]
  );

  // Sync current chat's tab with global activeTab when it changes externally
  React.useEffect(() => {
    if (lastResponsePairId && activeTab) {
      setConversationTabs(prev => {
        if (prev[lastResponsePairId] !== activeTab) {
          return {
            ...prev,
            [lastResponsePairId]: activeTab,
          };
        }
        return prev;
      });
    }
  }, [activeTab, lastResponsePairId]);

  return {
    conversationTabs,
    lastResponsePairId,
    getActiveTabForConversation,
    handleConversationTabChange,
  };
}
